import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Search, ChevronLeft, RefreshCw, Truck } from 'lucide-react';

const n   = v => Number(v) || 0;
const fmt = d => { try { return d ? new Date(d).toLocaleString('en-IN',{day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit'}) : '—'; } catch { return '—'; } };
const todayStr = () => new Date().toISOString().split('T')[0];

export default function DashboardDispatchReportPage() {
  const navigate = useNavigate();
  const [data, setData]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [from, setFrom]       = useState(todayStr());
  const [to, setTo]           = useState(todayStr());

  const load = useCallback((f, t) => {
    setLoading(true);
    api.get(`/reports/dashboard/dispatch-report?from=${f}&to=${t}`)
      .then(r => setData(r.data || []))
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(from, to); }, []); // eslint-disable-line

  const filtered = data.filter(row => {
    const q = search.toLowerCase();
    return !q
      || row.project_name?.toLowerCase().includes(q)
      || row.proj_code?.toLowerCase().includes(q);
  });

  // Column totals — Closing total is the true "pending dispatch" figure and
  // must equal the dashboard's pending total (both derive from the same query).
  const totals = filtered.reduce((acc, row) => {
    const opening = n(row.opening_qty);
    const todayIn = n(row.today_in);
    const todayOut = n(row.today_out);
    acc.opening += opening;
    acc.in     += todayIn;
    acc.out    += todayOut;
    acc.closing += Math.max(0, opening + todayIn - todayOut);
    return acc;
  }, { opening: 0, in: 0, out: 0, closing: 0 });

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <button onClick={()=>navigate('/admin')} className="p-2 rounded-xl hover:bg-gray-100 transition-colors">
          <ChevronLeft className="w-5 h-5 text-gray-500"/>
        </button>
        <div>
          <h1 className="text-xl font-black text-gray-900">Dispatch Daily Report</h1>
          <p className="text-xs text-gray-400">Per project · Opening + In − Out = Closing · {filtered.length} projects · <span className="text-indigo-600 font-semibold">{totals.closing} pending</span></p>
        </div>
        <button onClick={()=>load(from,to)} className="ml-auto p-2 rounded-xl hover:bg-gray-100 transition-colors">
          <RefreshCw className={`w-4 h-4 text-gray-400 ${loading?'animate-spin':''}`}/>
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm px-4 py-3 flex flex-wrap gap-3 items-center">
        <div className="flex items-center gap-2 flex-1 min-w-[200px] border border-gray-200 rounded-xl px-3 py-2">
          <Search className="w-4 h-4 text-gray-300 flex-shrink-0"/>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search project, code..."
            className="flex-1 text-sm outline-none text-gray-700 placeholder-gray-300"/>
        </div>
        <div className="flex items-center gap-2">
          <input type="date" value={from} onChange={e=>setFrom(e.target.value)}
            className="border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-600 outline-none"/>
          <span className="text-gray-300 text-sm">to</span>
          <input type="date" value={to} onChange={e=>setTo(e.target.value)}
            className="border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-600 outline-none"/>
          <button onClick={()=>load(from,to)}
            className="bg-sky-600 hover:bg-sky-700 text-white px-4 py-2 rounded-xl text-sm font-semibold">Apply</button>
        </div>
        <p className="text-xs text-gray-400 ml-auto">{filtered.length} of {data.length} projects</p>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-4 border-sky-400 border-t-transparent rounded-full animate-spin"/>
          </div>
        ) : filtered.length===0 ? (
          <div className="text-center py-16">
            <Truck className="w-12 h-12 text-gray-200 mx-auto mb-3"/>
            <p className="text-gray-400 font-semibold text-sm">No dispatch activity in this range</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[700px]">
              <thead>
                <tr className="border-b border-gray-100 bg-sky-50/40">
                  {['#','Project','Project Code','Items','Last Activity','Opening','In','Out','Closing','Status'].map(h=>(
                    <th key={h} className="text-left text-[11px] font-semibold text-gray-400 uppercase px-4 py-3 whitespace-nowrap tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map((row,idx)=>{
                  const opening = n(row.opening_qty);
                  const todayIn = n(row.today_in);
                  const todayOut= n(row.today_out);
                  const closing = Math.max(0, opening + todayIn - todayOut);
                  const fullyDispatched = closing===0 && (opening+todayIn)>0;
                  const hasChallanGap   = todayOut===0 && (opening+todayIn)>0;
                  return (
                    <tr key={row.project_id} className="hover:bg-sky-50/20 cursor-pointer transition-colors"
                      onClick={()=>navigate(`/admin/projects/${row.project_id}`)}>
                      <td className="px-4 py-3 text-xs text-gray-400 font-semibold">{idx+1}</td>
                      <td className="px-4 py-3 font-semibold text-gray-800 text-sm whitespace-nowrap">{row.project_name}</td>
                      <td className="px-4 py-3 font-mono text-xs text-gray-500">{row.proj_code || '—'}</td>
                      <td className="px-4 py-3 text-center text-xs font-semibold text-gray-600">{n(row.item_count) || '—'}</td>
                      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{fmt(row.last_done_at)}</td>
                      <td className="px-4 py-3 text-center font-bold text-gray-700">{opening>0?opening:'—'}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`text-sm font-bold ${todayIn>0?'text-sky-600':'text-gray-300'}`}>{todayIn>0?`↓ ${todayIn}`:'—'}</span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`text-sm font-bold ${todayOut>0?'text-emerald-600':'text-gray-300'}`}>{todayOut>0?`↑ ${todayOut}`:'—'}</span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`text-sm font-black ${closing>0?'text-indigo-700':'text-gray-300'}`}>{closing>0?closing:'—'}</span>
                      </td>
                      <td className="px-4 py-3">
                        {fullyDispatched
                          ? <span className="inline-flex text-xs font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700">✓ Dispatched</span>
                          : hasChallanGap
                            ? <span className="inline-flex text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Pending Dispatch</span>
                            : <span className="inline-flex text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">In Progress</span>
                        }
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-gray-200 bg-sky-50/60 font-bold">
                  <td className="px-4 py-3 text-xs text-gray-500 uppercase tracking-wide" colSpan={5}>Total · Pending Dispatch</td>
                  <td className="px-4 py-3 text-center text-gray-800">{totals.opening || '—'}</td>
                  <td className="px-4 py-3 text-center text-sky-700">{totals.in ? `↓ ${totals.in}` : '—'}</td>
                  <td className="px-4 py-3 text-center text-emerald-700">{totals.out ? `↑ ${totals.out}` : '—'}</td>
                  <td className="px-4 py-3 text-center text-indigo-700 text-base">{totals.closing || 0}</td>
                  <td className="px-4 py-3"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
